export const registration = {
  hero: {
    title: 'Join EachTeach',
    subtitle: 'Start your language learning journey with us'
  },

  // Step0: registration type branching
  step0: {
    title: 'Choose how to register',
    subtitle: 'Pick the option that fits you',
    individual: {
      title: 'Register as an individual',
      desc: 'For individual users joining language exchange'
    },
    org: {
      title: 'Register as an organization',
      desc: 'For admins of language schools, clubs, or solo tutors'
    },
    member: {
      title: 'Register as an organization member',
      desc: 'For those who have a monthly code from their organization'
    },
    haveAccount: 'Already have an account?',
    login: 'Log in'
  },

  // Google login
  google: {
    button: 'Continue with Google',
    or: 'or',
    processing: 'Redirecting to Google...'
  },

  // Organization registration flow
  org: {
    title: 'Register as an organization',
    steps: {
      account: 'Account',
      type: 'Type',
      details: 'Details',
      document: 'ID',
      terms: 'Terms'
    },
    type: {
      title: 'Select organization type',
      subtitle: 'Choose the type that best describes your organization',
      individual_tutor: 'Solo professional (e.g. private tutor)',
      individual_tutor_desc: 'An individual providing language instruction',
      language_school: 'Language institution (school / academy)',
      language_school_desc: 'A corporate language education provider',
      community: 'Language learning community (e.g. university club)',
      community_desc: 'A non-profit or semi-profit learning community'
    },
    details: {
      title: 'Organization details',
      subtitle: 'Used for matching and billing',
      nameLabel: 'Organization name *',
      namePlaceholder: 'e.g. EachTeach Language School',
      emailLabel: 'Organization email *',
      emailPlaceholder: 'contact@example.com',
      websiteLabel: 'Website URL',
      websitePlaceholder: 'https://example.com',
      snsLabel: 'Social media URL',
      snsPlaceholder: 'https://instagram.com/...',
      schedulingLabel: 'Scheduling mode',
      schedulingHint: 'Choose whether students or the manager drive scheduling (changeable later)',
      schedulingStudent: 'Student-driven (students submit preferred times)',
      schedulingManager: 'Manager-driven (the manager arranges schedules)'
    },
    document: {
      title: 'Upload an ID document',
      subtitle: 'To verify your organization, upload an ID or registration certificate',
      label: 'ID document file *',
      hint: 'JPEG / PNG / PDF, up to 10MB. Stored securely and privately.',
      uploading: 'Uploading...',
      uploaded: 'Uploaded',
      selectFile: 'Select file',
      changeFile: 'Change file'
    },
    terms: {
      title: 'Agree to the terms',
      subtitle: 'Please review before applying',
      agreePrefix: 'I agree to the ',
      termsLink: 'Terms of Service',
      and: ' and ',
      privacyLink: 'Privacy Policy',
      agreeSuffix: ''
    },
    submit: 'Apply for organization registration',
    submitting: 'Submitting...',
    back: 'Back',
    next: 'Next',
    success: {
      title: 'Your organization application was submitted',
      body: 'Please wait for approval by our team. We will email you once approved.',
      toLogin: 'Go to login'
    },
    validation: {
      typeRequired: 'Please select an organization type',
      detailsRequired: 'Please enter the organization name and email',
      urlInvalid: 'URLs must start with http(s)://',
      documentRequired: 'Please upload an ID document',
      termsRequired: 'Please agree to the Terms of Service and Privacy Policy',
      fileTooLarge: 'File must be 10MB or smaller',
      fileType: 'Please upload a JPEG, PNG, or PDF',
      uploadFailed: 'Upload failed. Please try again'
    }
  },

  // Organization member registration flow
  member: {
    codeTitle: 'Enter your organization code',
    codeSubtitle: 'Enter the monthly code provided by your organization',
    codeLabel: 'Organization code',
    codePlaceholder: 'e.g. AB12CD34',
    verify: 'Verify code',
    checking: 'Checking...',
    invalidCode: 'The code is invalid or has expired',
    verifiedOrg: 'Verified organization',
    planFree: 'Free',
    planPro: 'Pro',
    validUntil: 'Valid until',
    back: 'Back',
    noCode: "Don't have a code?",
    contactUs: 'Contact us'
  },

  // Shared: account creation
  account: {
    title: 'Create your account',
    subtitle: 'Set your email and password',
    emailLabel: 'Email address',
    passwordLabel: 'Password',
    passwordPlaceholder: '8+ characters',
    confirmLabel: 'Confirm password',
    confirmPlaceholder: 'Re-enter password',
    next: 'Next',
    processing: 'Processing...',
    passwordMismatch: 'Passwords do not match',
    passwordMin: 'Password must be at least 8 characters',
    signupFailed: 'Failed to create account',
    emailConfirmTitle: 'Confirmation email sent',
    emailConfirmBody: 'We sent a confirmation email. Click the link inside to activate your account.',
    emailConfirmSpam: 'If you do not receive it, please check your spam folder.',
    toLogin: 'Go to login'
  },


  form: {
    name: {
      label: 'Full Name',
      placeholder: 'Enter your full name'
    },
    
    gender: {
      label: 'Gender',
      placeholder: 'Select gender',
      female: 'Female',
      male: 'Male',
      other: 'Other'
    },
    
    nativeLanguage: {
      label: 'Native Language',
      placeholder: 'Select your native language'
    },
    
    secondNativeLanguage: {
      label: 'Second Native Language (Optional)',
      placeholder: 'Select second native language (if applicable)'
    },
    
    learningLanguage: {
      label: 'Language You Want to Learn',
      placeholder: 'Select language to learn'
    },
    
    secondLearningLanguage: {
      label: 'Second Language You Want to Learn (Optional)',
      placeholder: 'Select second language to learn (if applicable)'
    },
    
    country: {
      label: 'Country of Residence',
      placeholder: 'Enter your country'
    },
    
    timezone: {
      label: 'Timezone',
      placeholder: 'Select your timezone'
    },
    
    email: {
      label: 'Email Address',
      placeholder: 'your.email@example.com'
    },
    
    availability: {
      label: 'Available Days & Times',
      description: 'Select the time slots you are available for each day'
    },
    
    languages: {
      japanese: 'Japanese',
      korean: 'Korean',
      english: 'English',
      chinese: 'Chinese',
      spanish: 'Spanish',
      french: 'French',
      german: 'German',
      portuguese: 'Portuguese',
      italian: 'Italian',
      russian: 'Russian',
      arabic: 'Arabic',
      hindi: 'Hindi',
      thai: 'Thai',
      vietnamese: 'Vietnamese',
      other: 'Other'
    },
    
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    
    agreement: {
      terms: 'Terms of Service',
      and: ' and ',
      privacy: 'Privacy Policy',
      agree: ' I agree to the '
    },
    
    validation: {
      required: 'Please fill in all required fields',
      availability: 'Please select at least one day and time slot',
      terms: 'Please agree to the Terms of Service and Privacy Policy'
    },
    
    status: {
      success: 'Registration completed! We will contact you shortly.',
      error: 'An error occurred. Please try again.'
    },
    
    submit: {
      button: 'Submit Registration',
      submitting: 'Submitting...'
    },
    
    requiredNote: '* Required fields'
  }
};
